import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useCombinedStore } from "../store";
import Modal from "react-modal";
import api from "../services/api.service";
import * as Styles from "../styles/AddObservation.styles";
import alfredoIcon from "../assets/alfredo-icon.svg";

const AddObservationModal: React.FC<{
  isOpen: boolean;
  onRequestClose: () => void;
}> = ({ isOpen, onRequestClose }) => {
  const {
    subsidiaryDataZustand,
    orderDataZustand,
    setMessageZustand,
  } = useCombinedStore((state) => {
    return {
      setSubsidiaryDataZustand: state.setSubsidiaryDataZustand,
      subsidiaryDataZustand: state.subsidiaryDataZustand,
      orderDataZustand: state.orderDataZustand,
      setMessageZustand: state.setMessageZustand,
    };
  });
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const observations =
    subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodObservations?.Observations;
  const [manualObservation, setManualObservation] = useState("");
  const [selectedPrefixes, setSelectedPrefixes] = useState<
    Record<string, string>
  >({});
  const products = orderDataZustand.products ?? [];
  const selectedProducts =
    products.filter(
      (product: any) => product.productSelected || product.productSemiSelected
    ) || [];
  const rowsRef = useRef<(HTMLTableRowElement | null)[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({});
  const [filteredObservations, setFilteredObservations] = useState<any[]>(
    subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodObservations?.Observations || []
  );

  const handleSearchChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value.toUpperCase();
    setSearchValue(inputValue);

    let filteredObservations: any[] = [];

    if (inputValue === "") {
      filteredObservations = observations;
    } else {
      filteredObservations = observations.filter((observation: any) =>
        observation.observationName.startsWith(inputValue)
      );
    }

    if (filteredObservations.length > 0) {
      const searchRow = observations.findIndex((observation: any) => observation.observationName === filteredObservations[0].observationName);

      if (searchRow !== -1 && rowsRef.current[searchRow]) {
        rowsRef.current[searchRow]?.scrollIntoView({ behavior: "smooth" });
      }
    }
    setFilteredObservations(filteredObservations);
  };

  const handleCheckChange = (
    observationUuid: string,
    isChecked: boolean
  ) => {
    const defaultPrefix = dropdownOptions[0];
    setCheckedRows((prev) => ({
      ...prev,
      [observationUuid]: isChecked,
    }));
    setSelectedPrefixes((prev) => ({
      ...prev,
      [observationUuid]: isChecked ? defaultPrefix : "",
    }));
  };

  const handleDropdownChange = (
    event: ChangeEvent<HTMLSelectElement>,
    observationUuid: string
  ) => {
    const selectedPrefix = event.target.value;
    setSelectedPrefixes((prevPrefixes) => ({
      ...prevPrefixes,
      [observationUuid]: selectedPrefix,
    }));
  };

  const handleSubmit = async () => {
    if (Object.keys(checkedRows).length === 0 && manualObservation === "") {
      setMessageZustand("Selecione pelo menos uma observação!", "error");
      return;
    }
    selectedProducts?.forEach(async (product: any) => {
      const selectedObservations =
        subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodObservations?.Observations?.filter(
          (observation: any) => checkedRows[observation.observationUuid]
        );
      const productObservations = [
        ...(product.productObservations ?? []),
        ...selectedObservations.map((observation: any) => ({
          observationUuid: observation.observationUuid,
          observationName: `${selectedPrefixes[observation.observationUuid]} ${observation.observationName
            }`,
          observationPrice: observation.observationPrice,
        })),
      ];

      if (manualObservation !== "") {
        productObservations.push({
          observationUuid: "manual",
          observationName: manualObservation,
          observationPrice: 0,
        });
      }
      const orderInfoUpdated: any = {
        productCode: Number(product.productCode),
        productUuid: product.productUuid,
        productPrinted: product.productPrinted,
        productName: product.productName,
        productPrice:
          Number(product?.productPrice.replace(",", ".")) +
          selectedObservations.reduce(
            (acc: number, observation: any) =>
              acc + (observation.observationPrice || 0),
            0
          ),
        productObservations: productObservations,
      };

      if (product.people) {
        orderInfoUpdated.peopleNumber = product.people;
      }
      if (product.productComboInfo) {
        orderInfoUpdated.productComboInfo = product.productComboInfo;
      }

      try {
        await api.post(
          `addProductObservation/${product.productOrderUuid}`,
          orderInfoUpdated
        );
        setMessageZustand("Observações adicionadas com sucesso!", "success");
        onRequestClose();
        setManualObservation("");
        setSearchValue("");
      } catch (err) {
        setMessageZustand("Erro ao adicionar observações!", "error");
      }
    });
    setCheckedRows({});
    setSelectedPrefixes({});
    setFilteredObservations(
      subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodObservations?.Observations || []
    );
  };

  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      await handleSubmit();
    }
  }

  const dropdownOptions = [
    "Com",
    "Obs",
    "Sem",
    "Só",
    "Pouco",
    "Muito",
    "Extra",
  ];
  const handleClose = async () => {
    await setFilteredObservations(
      subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodObservations?.Observations || []
    );
    setCheckedRows({});
    setSelectedPrefixes({});
    setManualObservation("");
    setSearchValue("");
    onRequestClose();
  }
  useEffect(() => {
    if (isOpen) {
      // dá uma pequena folga para o modal terminar de montar
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={() => handleClose()}
      style={Styles.customStyles}
      contentLabel="Main Modal"
      ariaHideApp={false}
    >
      <Styles.ModalWrapper
        tabIndex={-1}
        ref={modalWrapperRef}
        onKeyDown={handleKeyDown}
      >
        <Styles.CloseButton onClick={() => handleClose()}>
          &times;
        </Styles.CloseButton>
        <Styles.ModalContent>
          <Styles.ModalHeader
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
            <Styles.ModalTitle>{`Adicione Observações`}</Styles.ModalTitle>
            <Styles.ModalDescription>
              {`Adicione observações para os produtos selecionados.`}
            </Styles.ModalDescription>
          </Styles.ModalHeader>
          <Styles.ModalBody
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .backgroundColor
            }
          >
            <Styles.RowContainer>
              <Styles.FieldLabel>
                Pesquise pelo Nome da Observação:
              </Styles.FieldLabel>
              <Styles.FieldContainer>
                <Styles.TextInput
                  color={
                    subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                      .buttonsColor
                  }
                >
                  <input
                    type="text"
                    placeholder=""
                    value={searchValue}
                    onChange={handleSearchChange}
                    ref={searchInputRef}
                  />
                </Styles.TextInput>
              </Styles.FieldContainer>
            </Styles.RowContainer>
            <Styles.RowContainer>
              <Styles.TableContainer>
                <Styles.ObservationsTable>
                  <thead>
                    <tr>
                      <th style={{ width: "20%" }}>Selecione</th>
                      <th style={{ width: "40%" }}>Nome</th>
                      <th style={{ width: "20%" }}>Preço</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredObservations.map((observation: any, index: number) => (
                      <tr
                        key={observation.observationUuid}
                        ref={(el) => (rowsRef.current[index] = el)}
                        onClick={() =>
                          handleCheckChange(
                            observation.observationUuid,
                            !checkedRows[observation.observationUuid] // Inverte o estado atual
                          )
                        }
                      >
                        <td>
                          <input
                            style={{ display: 'inline-block', width: 'calc(var(--u) * 20)', height: 'calc(var(--u) * 20)', position: 'relative', cursor: 'pointer' }}
                            type="checkbox"
                            checked={checkedRows[observation.observationUuid] || false}
                            onChange={(event) =>
                              handleCheckChange(
                                observation.observationUuid,
                                event.target.checked
                              )
                            }
                          />
                          {checkedRows[observation.observationUuid] && (
                            <Styles.DropDown
                              color="#FFFFFF"
                              value={selectedPrefixes[observation.observationUuid]}
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                              onChange={(event) =>
                                handleDropdownChange(event, observation.observationUuid)
                              }
                            >
                              {dropdownOptions.map((option, index) => (
                                <option key={index} value={option}>
                                  {option}
                                </option>
                              ))}
                            </Styles.DropDown>
                          )}
                        </td>
                        <td>
                          {checkedRows[observation.observationUuid]
                            ? `${selectedPrefixes[observation.observationUuid]} ${observation.observationName}`
                            : observation.observationName}
                        </td>
                        <td>{observation.observationPrice}</td>
                      </tr>
                    ))}
                  </tbody>
                </Styles.ObservationsTable>
              </Styles.TableContainer>
            </Styles.RowContainer>
            <Styles.RowContainer>
              <Styles.FieldLabel>
                Adicione uma Observação Manual:
              </Styles.FieldLabel>
              <Styles.FieldContainer>
                <Styles.TextInput
                  color={
                    subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                      .buttonsColor
                  }
                >
                  <input
                    type="text"
                    placeholder=""
                    value={manualObservation}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setManualObservation(e.target.value);
                    }}
                  />
                </Styles.TextInput>
              </Styles.FieldContainer>
            </Styles.RowContainer>
          </Styles.ModalBody>
          <Styles.ModalFooter
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Button onClick={() => handleClose()}>
              Cancelar
            </Styles.Button>
            <Styles.Button onClick={handleSubmit}>
              Adicionar Observações
            </Styles.Button>
          </Styles.ModalFooter>
        </Styles.ModalContent>
      </Styles.ModalWrapper>
    </Modal>
  );
};

export default AddObservationModal;
